import { z } from 'zod';
import { ERROR_CODES, type ErrorCode } from './errors';
import type { Role } from './enums';
import type { TenantSettings } from './settings';

/** Synced entities, in the order the client pushes dependent records (parents first). */
export const ENTITY_NAMES = [
  'animal',
  'event',
  'milkRecord',
  'dailyMilkUsage',
  'reminder',
  'healthProtocol',
  'buyer',
  'sale',
  'payment',
  'expenseCategory',
  'expense',
  'recurringExpense',
  'fileAsset',
  'partner',
  'partnerContribution',
  'shareGrant',
  'distribution',
  'projectionAssumptionSet',
  'projectionScenario',
] as const;
export type EntityName = (typeof ENTITY_NAMES)[number];

/** Device (Dexie) table and Postgres table per entity. */
export const ENTITY_TABLES: Record<EntityName, { local: string; sql: string }> = {
  animal: { local: 'animals', sql: 'animals' },
  event: { local: 'events', sql: 'events' },
  milkRecord: { local: 'milkRecords', sql: 'milk_records' },
  dailyMilkUsage: { local: 'dailyMilkUsage', sql: 'daily_milk_usage' },
  reminder: { local: 'reminders', sql: 'reminders' },
  healthProtocol: { local: 'healthProtocols', sql: 'health_protocols' },
  buyer: { local: 'buyers', sql: 'buyers' },
  sale: { local: 'sales', sql: 'sales' },
  payment: { local: 'payments', sql: 'payments' },
  expenseCategory: { local: 'expenseCategories', sql: 'expense_categories' },
  expense: { local: 'expenses', sql: 'expenses' },
  recurringExpense: { local: 'recurringExpenses', sql: 'recurring_expenses' },
  fileAsset: { local: 'fileAssets', sql: 'file_assets' },
  partner: { local: 'partners', sql: 'partners' },
  partnerContribution: { local: 'partnerContributions', sql: 'partner_contributions' },
  shareGrant: { local: 'shareGrants', sql: 'share_grants' },
  distribution: { local: 'distributions', sql: 'distributions' },
  projectionAssumptionSet: { local: 'projectionAssumptionSets', sql: 'projection_assumption_sets' },
  projectionScenario: { local: 'projectionScenarios', sql: 'projection_scenarios' },
};

export const MAX_PUSH_MUTATIONS = 100;
export const DEFAULT_PULL_LIMIT = 500;

export const mutationSchema = z.object({
  mutationId: z.uuid(),
  entity: z.string().min(1).max(40),
  op: z.enum(['upsert', 'delete']),
  id: z.uuid(),
  data: z.record(z.string(), z.unknown()).optional(),
  clientTs: z.iso.datetime({ offset: true }),
});
export type Mutation = z.infer<typeof mutationSchema>;

export const pushRequestSchema = z.object({
  deviceId: z.string().min(1).max(100),
  mutations: z.array(mutationSchema).max(MAX_PUSH_MUTATIONS),
});
export type PushRequest = z.infer<typeof pushRequestSchema>;

export interface MutationError {
  code: ErrorCode;
  message: string;
  field?: string;
}

export interface MutationResult {
  mutationId: string;
  status: 'applied' | 'duplicate' | 'rejected';
  serverSeq?: number;
  error?: MutationError;
}

export interface PushResponse {
  results: MutationResult[];
}

export const pullQuerySchema = z.object({
  cursor: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(DEFAULT_PULL_LIMIT).default(DEFAULT_PULL_LIMIT),
});

export interface PullChange {
  entity: EntityName;
  id: string;
  serverSeq: number;
  deleted: boolean;
  data: Record<string, unknown> | null;
}

export interface PullUser {
  id: string;
  name: string;
  role: Role;
}

export interface PullResponse {
  changes: PullChange[];
  cursor: number;
  hasMore: boolean;
  tenant: { name: string; settings: TenantSettings; updatedAt: string };
  users: PullUser[];
}

export const mutationErrorSchema = z.object({
  code: z.enum(ERROR_CODES),
  message: z.string(),
  field: z.string().optional(),
});
